import { Button } from '@trellis/roots/button'
import { Calendar } from '@trellis/roots/calendar'
import { useIsMobile } from '@trellis/roots/lib/useIsMobile'
import { Popover, PopoverContent, PopoverTrigger } from '@trellis/roots/popover'
import {
  getBasQuarter,
  getFinancialYear,
  getFinancialYearStartMonth,
} from '@trellis/shared'
import { format } from 'date-fns'
import { CalendarRange } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import type { DateRange } from 'react-day-picker'

import { useOrganization } from '@/hooks/useOrganization'
import { cn } from '@/lib/utils'

interface DateRangeFilterProps {
  value: DateRange | undefined
  onChange: (range: DateRange | undefined) => void
}

function parseDateParts(dateString: string): [number, number, number] {
  const [year, month, day] = dateString.split('-').map(Number)
  return [year, month, day]
}

/** Local-midnight Date for the picker — matches how react-day-picker/date-fns format(). */
function toLocalDate(dateString: string): Date {
  const [year, month, day] = parseDateParts(dateString)
  return new Date(year, month - 1, day)
}

export function toDateRange(startDate: string, endDate: string): DateRange {
  return { from: toLocalDate(startDate), to: toLocalDate(endDate) }
}

/**
 * One UTC calendar day before `dateString`, for feeding back into getFinancialYear /
 * getBasQuarter — both read UTC date fields, so the anchor must be constructed in UTC
 * space rather than local time (which drifts a calendar day in positive UTC offsets).
 */
function dayBeforeUtc(dateString: string): Date {
  const [year, month, day] = parseDateParts(dateString)
  return new Date(Date.UTC(year, month - 1, day - 1))
}

export function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()
  const calendarResizeObserverRef = useRef<ResizeObserver>(undefined)
  const [calendarWidth, setCalendarWidth] = useState<number>()

  /**
   * PopoverContent's width:auto shrink-wraps to its widest child's max-content size,
   * which includes the presets row even though it scrolls — an overflow-x-auto element
   * still reports its unwrapped width when an ancestor's width is auto. Capping at the
   * calendar's own measured width forces the presets row to actually overflow/scroll
   * instead of widening the popover to fit every preset button.
   *
   * A callback ref (rather than an effect keyed on `open`) is required here: Radix mounts
   * the popover content — and this calendar node — one render behind the `open` state
   * flip, so an effect watching `open` fires before the node exists.
   */
  const setCalendarNode = useCallback((node: HTMLDivElement | null) => {
    calendarResizeObserverRef.current?.disconnect()
    if (!node) return

    setCalendarWidth(node.offsetWidth)
    calendarResizeObserverRef.current = new ResizeObserver(([entry]) => {
      setCalendarWidth(entry.contentRect.width)
    })
    calendarResizeObserverRef.current.observe(node)
  }, [])

  const { data: organizationResult } = useOrganization()
  const country = organizationResult?.data?.country ?? null
  const startMonth = getFinancialYearStartMonth(country)

  const now = new Date()
  const thisFy = getFinancialYear(now, startMonth)
  const lastFy = getFinancialYear(dayBeforeUtc(thisFy.startDate), startMonth)

  const thisBasQuarter = getBasQuarter(now)
  const lastBasQuarter = getBasQuarter(dayBeforeUtc(thisBasQuarter.startDate))

  const presets = [
    { label: 'This FY', range: thisFy },
    { label: 'Last FY', range: lastFy },
    { label: 'This BAS quarter', range: thisBasQuarter },
    { label: 'Last BAS quarter', range: lastBasQuarter },
  ]

  const hasActiveFilter = value?.from !== undefined || value?.to !== undefined

  const activeStartDate = value?.from ? format(value.from, 'yyyy-MM-dd') : undefined
  const activeEndDate = value?.to ? format(value.to, 'yyyy-MM-dd') : undefined

  const matchedPreset = presets.find(
    (preset) =>
      activeStartDate === preset.range.startDate &&
      activeEndDate === preset.range.endDate,
  )

  const triggerLabel =
    value?.from && value?.to
      ? `${format(value.from, 'MMM d, yyyy')} – ${format(value.to, 'MMM d, yyyy')}`
      : value?.from
        ? `${format(value.from, 'MMM d, yyyy')} – …`
        : 'Filter by date'

  function handlePresetClick(preset: (typeof presets)[number]): void {
    onChange(toDateRange(preset.range.startDate, preset.range.endDate))
    setOpen(false)
  }

  function handleCalendarSelect(range: DateRange | undefined): void {
    onChange(range)
    if (range?.from && range?.to) {
      setOpen(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'justify-start gap-2 rounded-lg font-normal',
            !hasActiveFilter && 'text-muted-foreground',
          )}
        >
          <CalendarRange className="size-4 shrink-0" />
          <span>{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        style={calendarWidth ? { maxWidth: calendarWidth } : undefined}
        align="start"
        aria-label="Filter reports by date"
      >
        <div className="flex gap-1 overflow-x-auto border-b p-3 sm:flex-wrap sm:overflow-x-visible">
          {presets.map((preset) => (
            <Button
              key={preset.label}
              variant={matchedPreset?.label === preset.label ? 'default' : 'outline'}
              size="sm"
              className="shrink-0 whitespace-nowrap"
              onClick={() => handlePresetClick(preset)}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        <div ref={setCalendarNode} className="w-fit">
          <Calendar
            mode="range"
            selected={value}
            onSelect={handleCalendarSelect}
            numberOfMonths={isMobile ? 1 : 2}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
